import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import CloseIcon from "@mui/icons-material/Close"
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined"
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"
import type { FormikProps } from "formik"

import { InfoBlock } from "../../components/containers/InfoBlock"
import { InfoContainer } from "../../components/core/InfoContainer"
import { KeyValueBlock } from "../../components/dialogs/components/KeyValueBlock"
import { getTheme } from "../../hooks/UseTheme"
import type { ProjectFeatureMappingDialogProps } from "./interfaces"

import styles from "./Projects.module.scss"

export function ProjectFeatureMappingDialog(props: Readonly<ProjectFeatureMappingDialogProps>): React.ReactElement {
  const { isOpen, onClose, name, workflowSchema, workflowFormik, isLoading, isWorkflowInReadMode } = props

  const theme = getTheme()
  const MuiTheme = useTheme()

  const { t } = useTranslation("projects", {
    keyPrefix: "projectFeatureMappingDialog",
  })

  const mode = isWorkflowInReadMode ? "read" : "edit"

  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(5)

  return (
    <Dialog
      open={isOpen}
      maxWidth={"md"}
      fullWidth
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      onClose={onClose}
    >
      <DialogTitle className="dialog-header-base" display="flex" justifyContent="space-between">
        <Typography variant="h2-bold">{t("title")}</Typography>

        <IconButton
          onClick={() => {
            onClose()
          }}
          size="small"
          className={"close-icon-button"}
        >
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.featureMappingDialogContent}>
        <Grid container item xs={12} p={3} gap={1}>
          <Grid container item flexDirection={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Grid item xs height="fit-content" container justifyContent="flex-start">
              <Grid item className={styles.projectSideBar}>
                <TokenOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />
              </Grid>

              <Grid item xs container direction="column" gap={0.9}>
                <Grid item>
                  <Typography variant="label-bold" variantColor={2}>
                    {t("liveModelTitle")}
                  </Typography>
                </Grid>

                <Grid justifySelf="flex-end" item xs mt={"5px"} container>
                  {isLoading ? (
                    <Grid item mt={1}>
                      <Skeleton width={60} height={24} />
                    </Grid>
                  ) : (
                    <Typography variant="h3-bold">{name}</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Template info section (input features, output features, description, etc..) */}
          <Grid container item flexDirection="column">
            {isLoading ? (
              <Grid item xs={12} container minHeight={"80%"}>
                <InfoContainer icon={<CircularProgress />} title={t("loading.templateInfo")} />
              </Grid>
            ) : (
              <Grid item mt={1}>
                {/* Input Features */}
                <Typography variant="h3-bold" gutterBottom>
                  {t("modelInputFeatures.title")}
                </Typography>
                <Typography variant="p" variantColor={2} gutterBottom>
                  {t("modelInputFeatures.description")}
                </Typography>

                {isLoading ? (
                  <InfoContainer icon={<CircularProgress />} title={t("loading.featureMappings")} />
                ) : workflowFormik?.values.liveModelSchema?.length === 0 ? (
                  <Grid item xs={12} display={"flex"} mt={1}>
                    <InfoBlock text={t("noFeaturesFound")} bordered type="default" alignText="center" />
                  </Grid>
                ) : (
                  <Grid mt={1} height={346}>
                    <KeyValueBlock
                      isViewMode={mode === "read"}
                      inputFeatures={workflowFormik?.values.liveModelSchema}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formik={workflowFormik as FormikProps<any>}
                      valueListOptions={workflowSchema?.filter((feature) => feature?.name?.length > 0)}
                      type="Project"
                      enableSelfMappingUnmappedValues
                      paginationProps={{
                        usePagination: true,
                        variant: "complex",
                        page,
                        rowsPerPage,
                        onPageChange: setPage,
                        setRowsPerPage: setRowsPerPage,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
